import React, { useState, useEffect, useRef } from 'react';
import { X, Send, MessageSquare, Mail, Headset, ArrowLeft, Loader2, ChevronRight, Plus } from 'lucide-react';
import api from '../utils/api';
import authStore from '../store/authStore';

function timeAgo(d) {
  if (!d) return '';
  const s = Math.floor((new Date() - new Date(d)) / 1000);
  if (s < 60) return 'adesso';
  const m = Math.floor(s / 60); if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h fa`;
  return new Date(d).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const SupportPanel = ({ isOpen, onClose }) => {
  const { user } = authStore();
  const [view, setView] = useState('menu'); // menu, tickets, chat, new-ticket
  const [tickets, setTickets] = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [subject, setSubject] = useState('');
  const [firstMessage, setFirstMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    if (isOpen && user) {
      fetchTickets();
    }
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [isOpen]);

  const fetchTickets = async () => {
    try {
      const res = await api.get('/api/support/tickets');
      setTickets(res.data?.tickets || []);
    } catch {}
  };

  const openTicket = async (ticket) => {
    setActiveTicket(ticket);
    setView('chat');
    setLoading(true);
    try {
      const res = await api.get(`/api/support/tickets/${ticket.ticket_id}`);
      setMessages(res.data?.messages || []);
      setActiveTicket(res.data?.ticket || ticket);
      connectWebSocket(ticket.ticket_id);
    } catch {}
    finally { setLoading(false); }
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 200);
  };

  const connectWebSocket = (ticketId) => {
    if (wsRef.current) wsRef.current.close();
    const token = localStorage.getItem('token');
    if (!token) return;
    const configuredUrl = process.env.REACT_APP_BACKEND_URL || '';
    const base = configuredUrl.replace(/^http/, 'ws');
    const ws = new WebSocket(`${base}/api/ws/support/${ticketId}?token=${token}&role=user`);
    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          setMessages(prev => [...prev, data]);
          setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }
      } catch {}
    };
    wsRef.current = ws;
  };

  const handleCreateTicket = async () => {
    if (!firstMessage.trim()) return;
    setSending(true);
    try {
      const res = await api.post('/api/support/tickets', {
        subject: subject.trim() || 'Richiesta di supporto',
        message: firstMessage.trim(),
      });
      const ticketId = res.data?.ticket_id;
      if (ticketId) {
        await fetchTickets();
        const ticketRes = await api.get(`/api/support/tickets/${ticketId}`);
        setActiveTicket(ticketRes.data?.ticket);
        setMessages(ticketRes.data?.messages || []);
        connectWebSocket(ticketId);
        setView('chat');
        setSubject('');
        setFirstMessage('');
      }
    } catch (e) { console.error('Create ticket error:', e); }
    finally { setSending(false); }
  };

  const handleSendMessage = async () => {
    if (!newMsg.trim() || !activeTicket) return;
    setSending(true);
    try {
      await api.post(`/api/support/tickets/${activeTicket.ticket_id}/message`, { message: newMsg });
      setNewMsg('');
      if (!wsConnected) {
        const res = await api.get(`/api/support/tickets/${activeTicket.ticket_id}`);
        setMessages(res.data?.messages || []);
      }
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch {}
    finally { setSending(false); }
  };

  if (!isOpen) return null;

  const statusLabel = (s) => {
    if (s === 'open') return 'Aperto';
    if (s === 'resolved') return 'Risolto';
    if (s === 'closed') return 'Chiuso';
    return s;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-4" data-testid="support-panel">
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full sm:w-[400px] h-[85vh] sm:h-[600px] sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden sm:mr-4"
        data-testid="support-panel-content">

        {/* Header */}
        <div className="bg-[#1A1A2E] text-white px-4 py-3.5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            {(view === 'chat' || view === 'new-ticket' || view === 'tickets') && (
              <button onClick={() => {
                if (view === 'chat') { setView('tickets'); if (wsRef.current) wsRef.current.close(); }
                else setView('menu');
              }} className="p-1 rounded hover:bg-white/10" data-testid="support-panel-back">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <Headset className="w-5 h-5 text-[#10B981]" />
            <div>
              <p className="font-bold text-sm">
                {view === 'menu' ? 'Supporto' : view === 'tickets' ? 'Le tue richieste' : view === 'new-ticket' ? 'Nuova richiesta' : activeTicket?.subject || 'Chat'}
              </p>
              {view === 'chat' && wsConnected && <p className="text-[10px] text-emerald-400">Connesso</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition" data-testid="support-panel-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Menu view */}
        {view === 'menu' && (
          <div className="flex-1 p-5 space-y-3 overflow-y-auto">
            <div className="text-center mb-4">
              <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-3">
                <Headset className="w-8 h-8 text-[#10B981]" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Come possiamo aiutarti?</h3>
              <p className="text-sm text-gray-500 mt-1">Scegli come contattarci</p>
            </div>

            <button onClick={() => { setView('new-ticket'); }}
              className="w-full flex items-center gap-4 p-4 bg-[#10B981]/5 border-2 border-[#10B981]/20 rounded-2xl hover:border-[#10B981]/40 transition text-left"
              data-testid="support-new-chat-btn">
              <div className="w-12 h-12 rounded-xl bg-[#10B981] flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">Chat con il supporto</p>
                <p className="text-xs text-gray-500">Scrivi e ricevi risposte in tempo reale</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto flex-shrink-0" />
            </button>

            <a href="mailto:support@makehome360.com"
              className="w-full flex items-center gap-4 p-4 bg-blue-50 border-2 border-blue-100 rounded-2xl hover:border-blue-200 transition text-left"
              data-testid="support-email-btn">
              <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">Invia una email</p>
                <p className="text-xs text-gray-500">support@makehome360.com</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 ml-auto flex-shrink-0" />
            </a>

            {tickets.length > 0 && (
              <button onClick={() => setView('tickets')}
                className="w-full flex items-center gap-4 p-4 bg-gray-50 border-2 border-gray-100 rounded-2xl hover:border-gray-200 transition text-left"
                data-testid="support-view-tickets-btn">
                <div className="w-12 h-12 rounded-xl bg-gray-500 flex items-center justify-center flex-shrink-0">
                  <Headset className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">Le tue richieste ({tickets.length})</p>
                  <p className="text-xs text-gray-500">Vedi le conversazioni esistenti</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 ml-auto flex-shrink-0" />
              </button>
            )}
          </div>
        )}

        {/* Tickets list */}
        {view === 'tickets' && (
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            <button onClick={() => setView('new-ticket')}
              className="w-full flex items-center gap-2 p-3 bg-[#10B981] text-white rounded-xl font-bold text-sm justify-center mb-2"
              data-testid="support-new-ticket-from-list">
              <Plus className="w-4 h-4" /> Nuova richiesta
            </button>
            {tickets.map(ticket => (
              <div key={ticket.ticket_id}
                onClick={() => openTicket(ticket)}
                className="bg-gray-50 rounded-xl p-3.5 cursor-pointer hover:bg-gray-100 transition border border-gray-100"
                data-testid={`support-ticket-item-${ticket.ticket_id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-bold text-sm text-gray-900 flex-1 truncate">{ticket.subject}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg ${
                    ticket.status === 'open' ? 'bg-blue-100 text-blue-700' :
                    ticket.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-gray-200 text-gray-600'
                  }`}>{statusLabel(ticket.status)}</span>
                  {ticket.unread_user > 0 && (
                    <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{ticket.unread_user}</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">{ticket.last_message_by === 'admin' ? 'Supporto: ' : 'Tu: '}{ticket.last_message}</p>
                <p className="text-[11px] text-gray-400 mt-1">{timeAgo(ticket.updated_at)}</p>
              </div>
            ))}
          </div>
        )}

        {/* New ticket form */}
        {view === 'new-ticket' && (
          <div className="flex-1 p-5 space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Oggetto (opzionale)</label>
              <input
                value={subject}
                onChange={e => setSubject(e.target.value)}
                placeholder="Es: Problema con il profilo"
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="support-subject-input"
              />
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">Messaggio</label>
              <textarea
                value={firstMessage}
                onChange={e => setFirstMessage(e.target.value)}
                placeholder="Descrivi il tuo problema o la tua domanda..."
                rows={5}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-400 resize-none"
                data-testid="support-first-message-input"
              />
            </div>
            <button onClick={handleCreateTicket}
              disabled={sending || !firstMessage.trim()}
              className="w-full py-3 bg-[#10B981] text-white font-bold rounded-xl text-sm disabled:opacity-50 hover:bg-[#059669] transition flex items-center justify-center gap-2"
              data-testid="support-submit-ticket-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Invio...' : 'Invia messaggio'}
            </button>
          </div>
        )}

        {/* Chat view */}
        {view === 'chat' && (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="support-chat-messages">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
              ) : messages.length === 0 ? (
                <p className="text-center text-gray-400 py-8 text-sm">Inizio della conversazione</p>
              ) : (
                messages.map(msg => (
                  <div key={msg.message_id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${msg.sender === 'user' ? 'bg-[#10B981] text-white' : 'bg-gray-100 text-gray-900'}`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-[10px] mt-1 ${msg.sender === 'user' ? 'text-white/60' : 'text-gray-400'}`}>
                        {timeAgo(msg.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="border-t border-gray-100 p-3 flex gap-2 flex-shrink-0">
              <input
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                placeholder="Scrivi un messaggio..."
                className="flex-1 px-4 py-2.5 bg-gray-50 rounded-xl text-sm outline-none focus:ring-2 focus:ring-emerald-400 border border-gray-200"
                data-testid="support-chat-input"
              />
              <button onClick={handleSendMessage} disabled={sending || !newMsg.trim()}
                className="p-2.5 bg-[#10B981] text-white rounded-xl disabled:opacity-50 hover:bg-[#059669] transition"
                data-testid="support-chat-send-btn">
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SupportPanel;
